import api from "./api";

let generation = 0;
let pending = null;
let registeredAt = 0;
let registeredUserId = null;

const supported = () =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const decodeKey = value => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(padded), c => c.charCodeAt(0));

  if (bytes.length !== 65 || bytes[0] !== 4) {
    throw new Error("INVALID_VAPID_PUBLIC_KEY");
  }

  return bytes;
};

const register = async userId => {
  if (!supported() || Notification.permission !== "granted") return false;

  const token = localStorage.getItem("token");
  const targetUserId = Number(userId);

  if (
    !token ||
    !Number.isSafeInteger(targetUserId) ||
    targetUserId <= 0
  ) return false;

  // Renovar como maximo una vez cada 24 horas por usuario.
  if (
    registeredUserId === targetUserId &&
    Date.now() - registeredAt < 24 * 60 * 60 * 1000
  ) return true;

  const currentGeneration = generation;

  const { data } = await api.get("/push/public-key");

  if (
    generation !== currentGeneration ||
    localStorage.getItem("token") !== token
  ) return false;

  const key = decodeKey(data.publicKey);

  await navigator.serviceWorker.register("/mandaexpress-sw.js", {
    scope: "/"
  });

  const registration = await navigator.serviceWorker.ready;

  if (generation !== currentGeneration) return false;

  let subscription = await registration.pushManager.getSubscription();

  if (subscription?.options?.applicationServerKey) {
    const oldKey = new Uint8Array(
      subscription.options.applicationServerKey
    );

    const sameKey =
      oldKey.length === key.length &&
      oldKey.every((value, index) => value === key[index]);

    if (!sameKey) {
      await subscription.unsubscribe();
      subscription = null;
    }
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key
    });
  }

  if (
    generation !== currentGeneration ||
    localStorage.getItem("token") !== token
  ) return false;

  const response = await api.post("/push/subscriptions", {
    subscription: subscription.toJSON()
  });

  if (response.status !== 201 || response.data?.registered !== true) {
    throw new Error("PUSH_REGISTRATION_NOT_CONFIRMED");
  }

  if (
    generation !== currentGeneration ||
    localStorage.getItem("token") !== token
  ) return false;

  registeredUserId = targetUserId;
  registeredAt = Date.now();
  return true;
};

export const registerPushForCurrentUser = userId => {
  if (!pending) {
    const task = register(userId);

    pending = task;

    task.then(
      () => { if (pending === task) pending = null; },
      () => { if (pending === task) pending = null; }
    );
  }

  return pending;
};

export const unregisterPushOnLogout = async () => {
  generation += 1;
  registeredAt = 0;
  registeredUserId = null;

  if (pending) {
    try {
      await pending;
    } catch (_) {
      // Continuar con la cancelacion.
    }
  }

  if (!supported()) return;

  const registration =
    await navigator.serviceWorker.getRegistration("/");

  if (!registration?.pushManager) return;

  const subscription =
    await registration.pushManager.getSubscription();

  if (!subscription) return;

  const endpoint = subscription.endpoint;

  try {
    const cancelled = await subscription.unsubscribe();

    if (!cancelled) {
      console.warn("Web Push: cancelacion local pendiente");
    }
  } catch (_) {
    console.warn("Web Push: no se pudo cancelar localmente");
  }

  try {
    await api.delete("/push/subscriptions", {
      data: { endpoint }
    });
  } catch (_) {
    console.warn("Web Push: limpieza remota pendiente");
  }

  return endpoint;
};
